import React, { useState, useMemo } from 'react';

// Compact mock dictionary for demo
const mockDictionary = [
  { pt_code: "10028813", pt_name: "Nausea", llt_name: "Feeling sick", soc_name: "Gastrointestinal disorders", synonyms: ["queasy", "sick feeling"] },
  { pt_code: "10047700", pt_name: "Vomiting", llt_name: "Throwing up", soc_name: "Gastrointestinal disorders", synonyms: ["emesis"] },
  { pt_code: "10012735", pt_name: "Diarrhoea", llt_name: "Loose stools", soc_name: "Gastrointestinal disorders", synonyms: ["runny stool"] },
  { pt_code: "10000081", pt_name: "Abdominal pain", llt_name: "Stomach pain", soc_name: "Gastrointestinal disorders", synonyms: ["belly pain", "stomach ache"] },
  { pt_code: "10000096", pt_name: "Abdominal discomfort", llt_name: "Stomach upset", soc_name: "Gastrointestinal disorders", synonyms: ["upset stomach"] },
  { pt_code: "10019211", pt_name: "Headache", llt_name: "Head pain", soc_name: "Nervous system disorders", synonyms: ["cephalgia", "head ache"] },
  { pt_code: "10013573", pt_name: "Dizziness", llt_name: "Lightheadedness", soc_name: "Nervous system disorders", synonyms: ["woozy", "dizzy"] },
  { pt_code: "10044565", pt_name: "Tremor", llt_name: "Shaking", soc_name: "Nervous system disorders", synonyms: ["trembling"] },
  { pt_code: "10037844", pt_name: "Rash", llt_name: "Skin eruption", soc_name: "Skin disorders", synonyms: ["skin rash"] },
  { pt_code: "10037087", pt_name: "Pruritus", llt_name: "Itching", soc_name: "Skin disorders", synonyms: ["itchy"] },
  { pt_code: "10016256", pt_name: "Fatigue", llt_name: "Tiredness", soc_name: "General disorders", synonyms: ["tired", "exhaustion", "lethargy"] },
  { pt_code: "10037660", pt_name: "Pyrexia", llt_name: "Fever", soc_name: "General disorders", synonyms: ["high temperature"] },
  { pt_code: "10033557", pt_name: "Palpitations", llt_name: "Heart racing", soc_name: "Cardiac disorders", synonyms: ["racing heart"] },
  { pt_code: "10008479", pt_name: "Chest pain", llt_name: "Chest pain", soc_name: "Cardiac disorders", synonyms: ["chest discomfort"] },
  { pt_code: "10013968", pt_name: "Dyspnoea", llt_name: "Shortness of breath", soc_name: "Respiratory disorders", synonyms: ["breathlessness", "SOB"] },
  { pt_code: "10028411", pt_name: "Myalgia", llt_name: "Muscle pain", soc_name: "Musculoskeletal disorders", synonyms: ["muscle ache"] },
  { pt_code: "10022437", pt_name: "Insomnia", llt_name: "Difficulty sleeping", soc_name: "Psychiatric disorders", synonyms: ["sleeplessness", "trouble sleeping"] },
  { pt_code: "10047513", pt_name: "Vision blurred", llt_name: "Blurred vision", soc_name: "Eye disorders", synonyms: ["blurry vision"] },
];

const mockAEQueue = [
  { id: "AE-001", subject: "1001", term: "bad headache", severity: "Moderate", sae: false },
  { id: "AE-002", subject: "1001", term: "feeling sick", severity: "Mild", sae: false },
  { id: "AE-003", subject: "1002", term: "dizziness", severity: "Mild", sae: false },
  { id: "AE-004", subject: "1003", term: "severe chest pain", severity: "Severe", sae: true },
  { id: "AE-005", subject: "1002", term: "itchy rash", severity: "Moderate", sae: false },
  { id: "AE-006", subject: "1004", term: "extreme tiredness", severity: "Moderate", sae: false },
  { id: "AE-007", subject: "1005", term: "trouble sleeping", severity: "Mild", sae: false },
  { id: "AE-008", subject: "1003", term: "stomach upset", severity: "Mild", sae: false },
];

const calculateMatch = (verbatim, entry) => {
  const v = verbatim.toLowerCase().trim();
  const pt = entry.pt_name.toLowerCase();
  const llt = entry.llt_name.toLowerCase();
  const syn = entry.synonyms || [];

  if (v === pt) return { confidence: 100, type: "Exact PT" };
  if (v === llt) return { confidence: 98, type: "Exact LLT" };
  if (syn.some(s => s.toLowerCase() === v)) return { confidence: 95, type: "Synonym" };
  if (pt.includes(v) || v.includes(pt)) return { confidence: 90, type: "Partial" };
  if (llt.includes(v) || v.includes(llt)) return { confidence: 88, type: "Partial" };

  const vWords = v.split(/\s+/);
  const allWords = [...pt.split(/\s+/), ...llt.split(/\s+/), ...syn.flatMap(s => s.split(/\s+/))];
  const matching = vWords.filter(vw => allWords.some(tw => tw.includes(vw) || vw.includes(tw)));
  
  if (matching.length > 0) return { confidence: Math.round(50 + (matching.length / vWords.length) * 40), type: "Fuzzy" };
  return { confidence: 0, type: "None" };
};

const autoCode = (term) => mockDictionary
  .map(e => ({ ...e, ...calculateMatch(term, e) }))
  .filter(r => r.confidence > 0)
  .sort((a, b) => b.confidence - a.confidence)
  .slice(0, 4);

export default function MedDRADemo() {
  const [aeData] = useState(mockAEQueue);
  const [idx, setIdx] = useState(0);
  const [coded, setCoded] = useState([]);
  const [mobileView, setMobileView] = useState('coding'); // 'queue' | 'coding' | 'log'

  const ae = aeData[idx];
  const results = useMemo(() => ae ? autoCode(ae.term) : [], [ae]);
  const codedCount = coded.length;

  const accept = (match) => {
    setCoded(prev => [...prev, { aeId: ae.id, term: ae.term, codedTo: match.pt_name, confidence: match.confidence }]);
    const nextUncoded = aeData.findIndex((a, i) => i > idx && !coded.find(c => c.aeId === a.id));
    if (nextUncoded !== -1) setIdx(nextUncoded);
  };

  const isAECoded = (aeId) => coded.find(c => c.aeId === aeId);

  const ConfBadge = ({ c }) => {
    const cls = c >= 90 ? "bg-green-200 text-green-900" : c >= 70 ? "bg-yellow-200 text-yellow-900" : "bg-red-200 text-red-900";
    return <span className={`${cls} text-xs font-bold px-2 py-0.5 border border-black rounded`}>{c}%</span>;
  };

  return (
    <div className="border-2 border-black bg-neutral-50 text-sm rounded-lg overflow-hidden flex flex-col h-[520px] md:h-[520px]">
      {/* Header */}
      <div className="bg-black text-white px-3 md:px-4 py-2 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="font-bold text-xs md:text-sm">⚕ MedDRA AUTO-CODER</span>
          <span className="text-neutral-400 text-xs hidden md:inline">DEMO v1.0</span>
        </div>
        <div className="text-xs">
          <span className="text-green-400 font-bold">{codedCount}</span>
          <span className="text-neutral-400">/{aeData.length}</span>
        </div>
      </div>

      {/* Progress */}
      <div className="bg-neutral-200 px-3 md:px-4 py-1.5 border-b-2 border-black flex items-center gap-2 md:gap-3 text-xs flex-shrink-0">
        <span className="text-neutral-600 hidden md:inline">Progress:</span>
        <div className="flex-1 h-2 bg-neutral-300 border border-black rounded-sm">
          <div className="h-full bg-green-500 rounded-sm transition-all" style={{ width: `${(codedCount / aeData.length) * 100}%` }} />
        </div>
        <div className="flex gap-1">
          <button 
            onClick={() => setIdx(Math.max(0, idx - 1))} 
            disabled={idx === 0}
            className="px-2 py-0.5 bg-white border border-black hover:bg-neutral-100 disabled:opacity-30 rounded"
          >
            ←
          </button>
          <button 
            onClick={() => setIdx(Math.min(aeData.length - 1, idx + 1))} 
            disabled={idx === aeData.length - 1}
            className="px-2 py-0.5 bg-white border border-black hover:bg-neutral-100 disabled:opacity-30 rounded"
          >
            →
          </button>
        </div>
      </div>

      {/* Mobile Tab Navigation */}
      <div className="md:hidden flex border-b-2 border-black flex-shrink-0">
        <button
          onClick={() => setMobileView('queue')}
          className={`flex-1 py-2 text-xs font-bold border-r border-black ${mobileView === 'queue' ? 'bg-sky-100' : 'bg-white'}`}
        >
          Queue ({aeData.length - codedCount})
        </button>
        <button
          onClick={() => setMobileView('coding')}
          className={`flex-1 py-2 text-xs font-bold border-r border-black ${mobileView === 'coding' ? 'bg-sky-100' : 'bg-white'}`}
        >
          Coding
        </button>
        <button
          onClick={() => setMobileView('log')}
          className={`flex-1 py-2 text-xs font-bold ${mobileView === 'log' ? 'bg-sky-100' : 'bg-white'}`}
        >
          Log ({coded.length})
        </button>
      </div>

      {/* Main content area - Desktop: side by side, Mobile: tabbed */}
      <div className="flex flex-1 min-h-0">
        {/* Queue - Desktop: always visible, Mobile: tab */}
        <div className={`${mobileView === 'queue' ? 'flex' : 'hidden'} md:flex w-full md:w-48 border-r-0 md:border-r-2 border-black bg-white flex-col`}>
          <div className="bg-neutral-100 px-2 py-1 border-b border-black text-xs font-bold text-neutral-600 flex-shrink-0 hidden md:block">
            AE QUEUE
          </div>
          <div className="flex-1 overflow-y-auto">
            {aeData.map((a, i) => {
              const isCoded = isAECoded(a.id);
              return (
                <div 
                  key={a.id}
                  onClick={() => {
                    setIdx(i);
                    setMobileView('coding');
                  }}
                  className={`flex items-center gap-1.5 px-2 md:px-2 py-2 md:py-1.5 cursor-pointer border-b border-neutral-200 text-xs ${
                    i === idx ? 'bg-sky-100 border-l-4 border-l-black' : 
                    isCoded ? 'bg-green-50' : 'hover:bg-neutral-50'
                  }`}
                >
                  <span className={`w-2 h-2 md:w-1.5 md:h-1.5 rounded-full flex-shrink-0 ${isCoded ? 'bg-green-500' : a.sae ? 'bg-red-500' : 'bg-yellow-400'}`} />
                  <span className="text-neutral-400 w-14 md:w-12 flex-shrink-0">{a.id}</span>
                  <span className="flex-1 truncate">{a.term}</span>
                  {a.sae && <span className="text-red-600 font-bold text-[10px]">SAE</span>}
                  {isCoded && <span className="text-green-600">✓</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Main Panel - Desktop: always visible, Mobile: tab */}
        <div className={`${mobileView === 'coding' ? 'flex' : 'hidden'} md:flex flex-1 flex-col min-w-0`}>
          <div className="flex-1 p-2 md:p-3 overflow-y-auto">
            {ae && (
              <>
                {/* Current AE */}
                <div className={`border-2 mb-2 md:mb-3 rounded-lg ${ae.sae ? 'border-red-500 bg-red-50' : 'border-black bg-white'}`}>
                  <div className={`px-2 md:px-3 py-1.5 border-b text-xs font-bold flex justify-between rounded-t-md ${
                    ae.sae ? 'bg-red-100 border-red-300' : 'bg-neutral-100 border-black'
                  }`}>
                    <span className="truncate">{ae.id} | Subj {ae.subject}</span>
                    {ae.sae && <span className="text-red-700 flex-shrink-0">⚠ SAE</span>}
                  </div>
                  <div className="p-2 md:p-3">
                    <div className="text-[10px] text-neutral-500 uppercase mb-0.5">Verbatim AE Term</div>
                    <div className="text-sm md:text-base font-bold border-b-2 border-green-500 inline-block">{ae.term}</div>
                    <div className="mt-2 text-xs text-neutral-500">
                      Severity: <span className={`font-bold ${
                        ae.severity === 'Severe' ? 'text-red-600' : 
                        ae.severity === 'Moderate' ? 'text-yellow-600' : 'text-green-600'
                      }`}>{ae.severity}</span>
                    </div>
                  </div>
                </div>

                {/* Results */}
                <div className="border-2 border-black bg-white rounded-lg">
                  <div className="bg-neutral-100 px-2 md:px-3 py-1.5 border-b border-black text-xs font-bold flex justify-between rounded-t-md">
                    <span>MATCHES</span>
                    <span className="text-neutral-400">{results.length} found</span>
                  </div>
                  {results.length > 0 ? (
                    <div>
                      {results.map((m, i) => (
                        <div 
                          key={i} 
                          className={`px-2 md:px-3 py-2 border-b border-neutral-200 last:border-b-0 ${
                            i === 0 ? 'bg-green-50' : ''
                          } hover:bg-neutral-50`}
                        >
                          {/* Mobile: stacked layout */}
                          <div className="flex flex-col md:flex-row md:items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-sm">{m.pt_name}</span>
                                <span className="text-neutral-400 text-xs">{m.pt_code}</span>
                              </div>
                              <div className="text-xs text-neutral-500 truncate">
                                {m.llt_name} | {m.soc_name}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 justify-between md:justify-end">
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] bg-neutral-200 px-1 border border-neutral-400 rounded">{m.type}</span>
                                <ConfBadge c={m.confidence} />
                              </div>
                              <button 
                                onClick={() => accept(m)}
                                disabled={isAECoded(ae.id)}
                                className="px-3 py-1 bg-black text-white text-xs font-bold border-2 border-black hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed rounded flex-shrink-0"
                              >
                                {isAECoded(ae.id) ? 'CODED' : 'ACCEPT'}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-4 md:p-6 text-center text-neutral-400">
                      <div className="text-2xl mb-1">⚠</div>
                      <div className="text-xs">No automatic matches found</div>
                      <div className="text-[10px]">Manual coding required</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Audit Log - Mobile: tab view, Desktop: bottom panel */}
        <div className={`${mobileView === 'log' ? 'flex' : 'hidden'} md:hidden w-full bg-white flex-col`}>
          <div className="flex-1 overflow-y-auto">
            {coded.length === 0 ? (
              <div className="h-full flex items-center justify-center text-neutral-400 text-xs p-4">
                No coding decisions recorded yet
              </div>
            ) : (
              coded.map((c, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2 text-xs border-b border-neutral-100">
                  <span className="text-neutral-400 flex-shrink-0">{c.aeId}</span>
                  <span className="flex-1 truncate">{c.term}</span>
                  <span>→</span>
                  <span className="font-bold text-green-700 truncate max-w-20">{c.codedTo}</span>
                  <ConfBadge c={c.confidence} />
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Desktop Audit Log - fixed at bottom */}
      <div className="hidden md:flex border-t-2 border-black bg-white flex-col flex-shrink-0 h-[100px]">
        <div className="bg-neutral-100 px-3 py-1 border-b border-black text-xs font-bold flex-shrink-0">
          AUDIT LOG ({coded.length} records)
        </div>
        <div className="flex-1 overflow-y-auto">
          {coded.length === 0 ? (
            <div className="h-full flex items-center justify-center text-neutral-400 text-xs">
              No coding decisions recorded yet
            </div>
          ) : (
            coded.map((c, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-1 text-xs border-b border-neutral-100">
                <span className="text-neutral-400">{c.aeId}</span>
                <span className="flex-1 truncate">{c.term}</span>
                <span>→</span>
                <span className="font-bold text-green-700">{c.codedTo}</span>
                <ConfBadge c={c.confidence} />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-neutral-200 px-3 py-1 text-[10px] text-neutral-500 border-t-2 border-black flex items-center flex-shrink-0">
        Interactive mockup for demonstration purposes only.
      </div>
    </div>
  );
}
